import { createFileRoute, Link, useNavigate, redirect } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import PageGray from '../components/General/PageGray';
import H1 from '../components/Typography/Headings/H1';
import { Card } from '@testbusters/card';
import { Icon } from '@testbusters/icon';
import { Button } from '@testbusters/button';
import Span from '../components/Typography/Span';
import RuleBox from '../components/SimulationFlow/RuleBox';
import SurveyShell from '../components/Survey/SurveyShell';
import SurveyQuestionStep from '../components/Survey/SurveyQuestionStep';
import SurveyResults from '../components/Survey/SurveyResults';
import {
  useGetSurvey,
  useGetSurveyResults,
  useSubmitSurveyReview,
  type ApiSurvey,
  type ApiSurveyResults,
} from '../hooks/surveys/useSurveys';
import Loader from '../components/Loader/Loader';
import GeneralError from '../components/Errors/General';

interface SurveySearch {
  view?: 'summary';
}

export const Route = createFileRoute('/orientamento/$surveyId')({
  beforeLoad: () => {
    if (import.meta.env.VITE_SURVEY_ENABLED !== 'true') {
      throw redirect({ to: '/' });
    }
  },
  component: SurveyPage,
  validateSearch: (search: Record<string, unknown>): SurveySearch => ({
    view: search.view === 'summary' ? 'summary' : undefined,
  }),
});

// 'summary' shows the user's most recent (already-submitted) review;
// 'results' shows the review just submitted in this session.
type Step = 'intro' | { questionIndex: number } | 'results' | 'summary';

/** Flattened (section, question) pairs in display order — one screen per question. */
interface FlatQuestion {
  sectionIndex: number;
  questionIndex: number;
}

const key = (sectionIndex: number, questionIndex: number) =>
  `${sectionIndex}-${questionIndex}`;

function SurveyPage() {
  const { surveyId } = Route.useParams();
  const { view } = Route.useSearch();
  const navigate = useNavigate();
  const { survey, isFetching } = useGetSurvey(surveyId);
  // Only fetch the existing review when we actually need the summary view.
  const { results: existingResults, isFetching: isFetchingResults } =
    useGetSurveyResults(view === 'summary' ? surveyId : '');

  const [step, setStep] = useState<Step>(view === 'summary' ? 'summary' : 'intro');
  // Selected answer indices keyed by `${sectionIndex}-${questionIndex}`.
  const [selections, setSelections] = useState<Record<string, number[]>>({});
  const [results, setResults] = useState<ApiSurveyResults | null>(null);

  const submit = useSubmitSurveyReview(surveyId);

  const restart = () => {
    setSelections({});
    setResults(null);
    setStep({ questionIndex: 0 });
    // Drop ?view=summary so a mid-retake refresh doesn't bounce back to the summary.
    if (view === 'summary') {
      navigate({ to: '/orientamento/$surveyId', params: { surveyId }, search: {}, replace: true });
    }
  };

  const flatQuestions: FlatQuestion[] = useMemo(
    () =>
      (survey?.sections ?? []).flatMap((section, sectionIndex) =>
        section.questions.map((_, questionIndex) => ({ sectionIndex, questionIndex })),
      ),
    [survey],
  );

  if (isFetching && !survey) return <Loader fullScreen />;
  if (!survey) return <GeneralError />;

  // Focused view of the user's most recent review (opened from "Vedi il riepilogo").
  if (step === 'summary') {
    if (isFetchingResults && !existingResults) return <Loader fullScreen />;
    if (!existingResults) return <GeneralError />;
    return (
      <SurveyShell>
        <SurveyResults
          results={existingResults}
          onRepeat={restart}
          onPrepare={() => navigate({ to: '/simulazioni' })}
        />
      </SurveyShell>
    );
  }

  if (step === 'intro') {
    return <SurveyIntro survey={survey} onStart={() => setStep({ questionIndex: 0 })} />;
  }

  if (step === 'results' && results) {
    return (
      <SurveyShell>
        <SurveyResults
          results={results}
          onRepeat={restart}
          onPrepare={() => navigate({ to: '/simulazioni' })}
        />
      </SurveyShell>
    );
  }

  // Question step (step is either 'results' with no data, or { questionIndex }).
  const current = typeof step === 'object' ? step.questionIndex : 0;
  const flat = flatQuestions[current];
  if (!flat) return <GeneralError />;
  const question = survey.sections[flat.sectionIndex].questions[flat.questionIndex];
  const selected = selections[key(flat.sectionIndex, flat.questionIndex)] ?? [];
  const isLast = current === flatQuestions.length - 1;

  const handleConfirm = () => {
    if (!isLast) {
      setStep({ questionIndex: current + 1 });
      return;
    }
    // Build the submission payload in section/question order.
    const body = {
      sections: survey.sections.map((section, sectionIndex) => ({
        questions: section.questions.map((_, questionIndex) => ({
          given: selections[key(sectionIndex, questionIndex)] ?? [],
        })),
      })),
    };
    submit.mutate(body, {
      onSuccess: (data) => {
        setResults(data);
        setStep('results');
      },
    });
  };

  const handleBack = () => {
    if (current === 0) setStep('intro');
    else setStep({ questionIndex: current - 1 });
  };

  return (
    <SurveyShell>
      <SurveyQuestionStep
        key={current}
        question={question}
        selected={selected}
        onChange={(next) =>
          setSelections((prev) => ({
            ...prev,
            [key(flat.sectionIndex, flat.questionIndex)]: next,
          }))
        }
        onBack={handleBack}
        onConfirm={handleConfirm}
        isLast={isLast}
        isSubmitting={submit.isPending}
      />
    </SurveyShell>
  );
}

function SurveyIntro({ survey, onStart }: { survey: ApiSurvey; onStart: () => void }) {
  const { t } = useTranslation();

  const sectionRules = [
    {
      icon: 'comment-question' as const,
      title: t('orientamento.sections.professioni'),
      description: t('orientamento.sections.professioniDesc'),
    },
    {
      icon: 'user' as const,
      title: t('orientamento.sections.attitudiniPersonali'),
      description: t('orientamento.sections.attitudiniPersonaliDesc'),
    },
    {
      icon: 'progress-positive' as const,
      title: t('orientamento.sections.attitudiniLavorative'),
      description: t('orientamento.sections.attitudiniLavorativeDesc'),
    },
  ];

  return (
    <PageGray className="min-h-screen flex flex-col justify-center gap-8 lg:gap-11 px-4! py-6! lg:px-11! lg:py-8!">
      <Link
        to="/orientamento"
        className="flex self-end fixed top-4 right-4 lg:top-8 lg:right-11"
      >
        <div className="flex items-center gap-2 w-fit">
          <Span className="first-letter:uppercase">{t('common.close')}</Span>
          <Icon name="close" className="text-2xl" />
        </div>
      </Link>

      <div className="flex flex-col items-center">
        <div className="flex flex-col items-center gap-8 w-full">
          <img src="/icons/questionmark.svg" width={40} height={40} />

          <H1 className="text-center">{t('orientamento.survey.title')}</H1>

          <Card className="rounded-lg p-4 lg:p-7 shadow-none bg-white flex items-center gap-4 w-full">
            <Icon name="clock" className="text-2xl text-icon-general-accent" />
            <Span className="font-semibold">
              {survey.sections.length} {t('common.questions')}
            </Span>
          </Card>

          <div className="flex flex-col sm:flex-row justify-center gap-4 lg:gap-x-7 w-full">
            {sectionRules.map((s) => (
              <RuleBox
                key={s.title}
                icon={s.icon}
                title={s.title}
                description={s.description}
              />
            ))}
          </div>

          <Button className="min-w-60" onClick={onStart}>
            {t('orientamento.survey.initButton')}
          </Button>
        </div>
      </div>
    </PageGray>
  );
}
